import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Icon from "../../icons/inner-pages-icons";
import FollowUpPopup from "../../../pages/mainDeals/detail-Page/follow-ups";
import { DynamicTable } from "../../Popup-comp";
import {
  Calendar,
  ChartNoAxesColumnIncreasing,
  ChartNoAxesCombined,
  ClipboardType,
  Clock,
  DollarSign,
  Key,
  NotebookPen,
} from "lucide-react";
import Swal from "sweetalert2";
import { useApiCall } from "../../custom-hooks/api-call";
import MarkCompleted from "./mark";

const activityIcons = {
  call: "phone",
  phone: "phone",
  email: "mail",
  sms: "sms",
  consultation: "consultation",
  default: "default",

  note: "note",
  meeting: "calendar",
  "follow-up": "calendar",
  followup: "calendar",
  stage: "arrowRight",
  voicemail: "mic",
  chat: "sms",
  comment: "sms",
  demo: "demo",
  messasge: "sms",
  presentation: "presentation",
  assign: "assign",
  unassign: "unassign",
  file: "file",
  brief: "brief",
};

const activityColors = {
  call: "#3b82f6", // blue
  email: "#8b5cf6", // violet
  sms: "#06b6d4", // cyan
  consultation: "#f59e0b", // amber
  note: "#f59e0b", // amber
  meeting: "#10b981", // emerald
  stage: "#6366f1", // indigo
  // "follow-up": "#6366f1", // indigo
  "follow-up": "#06b6d4", // indigo
  voicemail: "#ef4444", // red

  chat: "#0ea5e9", // sky blue
  presentation: "#14b8a6", // teal
  demo: "#a855f7", // purple
  default: "#a855f7", // purple
  messasge: "#f43f5e", // rose

  // remaining icons (added)
  phone: "#3b82f6", // blue
  comment: "#0ea5e9", // sky
  followup: "#06b6d4", // indigo
  assign: "#22c55e", // green
  unassign: "#ef4444", // red
  file: "#eab308", // slate
  brief: "#eab308", // yellow
};

const T = {
  bg: "#EDE8E1",
  sidebar: "#1a1730",
  sidebarHover: "#252240",
  sidebarActive: "#302d4a",
  card: "#FFFFFF",
  cardBorder: "rgba(0,0,0,0.08)",
  text: "#1a1520",
  textSecondary: "#5c5466",
  textMuted: "#908892",
  accent: "#6366f1",
  accentLight: "rgba(99,102,241,0.08)",
  accentText: "#5457e5",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
  shadowLg: "0 8px 30px rgba(0,0,0,0.08)",
  inputBg: "#F3EFE9",
  inputBorder: "rgba(0,0,0,0.1)",
};

const Card = ({
  children,
  style = {},
  hover = false,
  onClick,
  className = "",
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setHovered(true)}
      onMouseLeave={() => hover && setHovered(false)}
      className={className}
      style={{
        background: T.card,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 12,
        boxShadow: hovered ? T.shadowMd : T.shadow,
        transform: hovered ? "translateY(-1px)" : "none",
        transition: "all 0.15s ease",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const priorityColors = {
  high: "#ef4444",
  medium: "#f59e0b",
  low: "#10b981",
  pending: "#f59e0b",
  completed: "#10b981",
};

const Badge = ({ color, children, style = {} }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: color + "14",
      color,
      letterSpacing: "0.3px",
      ...style,
    }}
  >
    {children}
  </span>
);

const truncateText = (text = "", max = 30) => {
  if (!text) return "";
  return text.length > max ? text.substring(0, max) + "..." : text;
};

const ActionButton = ({
  icon,
  label,
  color = T.accent,
  variant = "outline",
  onClick,
  style = {},
}) => {
  const [hovered, setHovered] = useState(false);
  const filled = variant === "filled";
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 16px",
        borderRadius: 10,
        border: filled ? "none" : `1px solid ${color}30`,
        background: filled
          ? hovered
            ? color
            : color + "ee"
          : hovered
            ? color + "10"
            : color + "06",
        color: filled ? "#fff" : color,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 600,
        fontFamily: "inherit",
        transition: "all 0.15s",
        width: "100%",
        boxShadow: filled ? `0 2px 8px ${color}35` : "none",
        ...style,
      }}
    >
      <Icon name={icon} size={15} color={filled ? "#fff" : color} /> {label}
    </button>
  );
};

export const PopupTimeline = ({ timeline = [] }) => {
  return (
    <div>
      {timeline.length ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
              position: "relative",
            }}
          >
            {/* Timeline items */}
            <div style={{ position: "relative", paddingLeft: 30 }}>
              {/* Vertical line */}
              <div
                style={{
                  position: "absolute",
                  left: 11,
                  top: 27,
                  bottom: 45,
                  width: 2,
                  background: "#ddd8d2",
                  borderRadius: 1,
                }}
              />

              {timeline?.map((a, i) => {
                const color =
                  activityColors[a?.icon?.toLocaleLowerCase()] ||
                  "#8b5cf6" ||
                  "#6b7280";
                return (
                  <div
                    key={a.id}
                    style={{ position: "relative", marginBottom: 12 }}
                  >
                    {/* Dot */}
                    <div
                      className="before:w-full before:h-full before:bg-white before:absolute before:rounded-full before:-z-10"
                      style={{
                        position: "absolute",
                        left: -30,
                        top: 19,
                        width: 25,
                        height: 25,
                        borderRadius: "50%",
                        background: color + "80",
                        border: `1.5px solid ${color}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                      }}
                    >
                      <Icon
                        name={
                          activityIcons[
                            a?.icon?.toLocaleLowerCase() || "default"
                          ]
                        }
                        size={13}
                        color={color}
                      />
                    </div>

                    <Card hover style={{ padding: "14px 18px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {a.log}
                            </span>

                            <span
                              style={{
                                fontSize: 11,
                                color: T.textMuted,
                                flexShrink: 0,
                              }}
                            >
                              {a?.logtime}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 12,
                              marginTop: 5,
                              fontSize: 11,
                              color: T.textMuted,
                            }}
                          >
                            <span>by {a.user.name}</span>
                            {/* {a.duration && <span>{a.duration}</span>} */}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <p className="text-gray-400 text-sm italic center">
          No timeline available.
        </p>
      )}
    </div>
  );
};

// export const FollowUpsTab = ({
//   leadDetail,
//   onError,
//   onSubmit,
//   UsersList = [],
//   id = "",
//   entitytype = "",
//   entityname = "",
// }) => {
//   const [showActivityModal, setShowActivityModal] = useState(false);
//   const [selectedActivity, setSelectedActivity] = useState(null);
//   return (
//     <>
//       <div className=" flex flex-col gap-[10px]  w-full h-full overflow-auto">
//         <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
//           <Card
//             style={{
//               padding: "12px 16px",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//             }}
//           >
//             <span style={{ fontSize: 13, fontWeight: 600 }}>
//               Scheduled Follow-Ups
//             </span>
//             <ActionButton
//               icon="plus"
//               label="New Follow-Up"
//               variant="filled"
//               style={{ width: "auto", padding: "7px 14px", fontSize: 12 }}
//               onClick={() => {
//                 if (typeof setSelectedActivity === "function") {
//                   setSelectedActivity(null);
//                 }
//                 if (typeof setShowActivityModal === "function") {
//                   setShowActivityModal(true);
//                 }
//               }}
//             />
//           </Card>
//           {leadDetail?.schedules?.length ? (
//             leadDetail?.schedules?.map((f) => {
//               const color =
//                 activityColors[f?.type?.toLocaleLowerCase()] ||
//                 "#8b5cf6" ||
//                 "#6b7280";
//               return (
//                 <Card
//                   key={f.id}
//                   hover
//                   style={{ padding: "14px 18px" }}
//                   onClick={() => {
//                     setSelectedActivity(f);
//                     setShowActivityModal(true);
//                   }}
//                 >
//                   <div
//                     style={{ display: "flex", alignItems: "center", gap: 14 }}
//                   >
//                     {/* <div
//                     style={{
//                       width: 10,
//                       height: 10,
//                       borderRadius: "50%",
//                       background:
//                         f.status?.toLowerCase() === "completed"
//                           ? "#22c55e"
//                           : priorityColors[f.priority],
//                       boxShadow:
//                         f.status?.toLowerCase() === "completed"
//                           ? "none"
//                           : `0 0 0 3px ${priorityColors[f.priority]}20`,
//                     }}
//                   /> */}
//                     <div
//                       style={{
//                         width: 32,
//                         height: 32,
//                         borderRadius: 8,
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         background: color + "12",
//                         flexShrink: 0,
//                       }}
//                     >
//                       <Icon
//                         name={
//                           activityIcons[
//                             f?.type?.toLocaleLowerCase() || "default"
//                           ]
//                         }
//                         size={14}
//                         color={color}
//                       />
//                     </div>
//                     <div style={{ flex: 1 }}>
//                       <div
//                         style={{
//                           fontSize: 13,
//                           fontWeight: 600,
//                           color: T.text,
//                           textDecoration:
//                             f.status.toLowerCase() === "completed"
//                               ? "line-through"
//                               : "none",
//                           opacity: f.status === "completed" ? 0.6 : 1,
//                         }}
//                         className="capitalize"
//                       >
//                         {f?.type}
//                       </div>
//                       <div
//                         style={{
//                           display: "flex",
//                           gap: 12,
//                           marginTop: 4,
//                           fontSize: 11,
//                           color: T.textMuted,
//                         }}
//                       >
//                         <span
//                           style={{
//                             display: "flex",
//                             alignItems: "center",
//                             gap: 4,
//                           }}
//                         >
//                           <Icon name="calendar" size={11} color={T.textMuted} />{" "}
//                           {f?.due_date}
//                         </span>
//                         <span
//                           style={{
//                             display: "flex",
//                             alignItems: "center",
//                             gap: 4,
//                           }}
//                         >
//                           <Icon name="clock" size={11} color={T.textMuted} />{" "}
//                           {f?.due_time}
//                         </span>
//                         <span>
//                           <Badge color={priorityColors[f.priority]}>
//                       {f.priority}
//                     </Badge>
//                         </span>
//                       </div>
//                     </div>
//                     <Badge color={priorityColors[f.status]}>
//                       {f.status}
//                     </Badge>
//                   </div>
//                 </Card>
//               );
//             })
//           ) : (
//             <p className="text-gray-400 text-sm italic text-center">
//               No scheduled activities.
//             </p>
//           )}
//         </div>

//         {/* ✅ Modal Integration */}
//         {showActivityModal && (
//           <FollowUpPopup
//             onClose={() => {
//               setShowActivityModal(false);
//               setSelectedActivity({});
//             }}
//             CustomerData={selectedActivity}
//             tabIndex={2}
//             onError={onError}
//             onSubmit={onSubmit}
//             id={id}
//             entitytype={entitytype}
//             entityname={entityname}
//             UsersList={UsersList}
//           />
//         )}
//       </div>
//     </>
//   );
// };

export const FollowUpsTab = ({
  leadDetail,
  onError,
  onSubmit,
  UsersList = [],
  id = "",
  entitytype = "",
  entityname = "",
  followUpID,
}) => {
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const [schedules, setSchedules] = useState([]);

  const followUpRefs = useRef({});

  useEffect(() => {
    // console.log("follow Up iud", followUpID);

    if (followUpID && followUpRefs.current[followUpID]) {
      followUpRefs.current[followUpID].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [followUpID, leadDetail, followUpRefs.current]); // wait until schedules loaded

  useEffect(() => {
    setSchedules(leadDetail?.schedules);
  }, [leadDetail]);

  const [loading, setLoading] = useState(false);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [current, setCurrent] = useState("");

  const apiCall = useApiCall();

  // const handleMarkCompleted = async (followUpID) => {
  //   setCurrent(followUpID);
  //   const result = await Swal.fire({
  //     title: "Are you sure?",
  //     text: "You want to mark this follow-up as completed?",
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonText: "Yes",
  //     cancelButtonText: "No",
  //   });

  //   if (result.isConfirmed) {
  //     try {
  //       setLoading(true);
  //       // Replace with your API endpoint and payload
  //       // await axios.post(`activities/update-followup-status`);

  //       let apiRes = await apiCall({
  //         method: "post",
  //         url: "activities/update-followup-status",
  //         data: {
  //           id: followUpID,
  //           status: "completed", //"pending"
  //         },
  //       });

  //       Swal.fire(
  //         "Completed!",
  //         "Follow-up has been marked completed.",
  //         "success",
  //       );

  //       // Update local schedules state
  //       setSchedules((prev) =>
  //         prev.map((f) =>
  //           f.id === followUpID ? { ...f, status: "completed" } : f,
  //         ),
  //       );
  //     } catch (error) {
  //       Swal.fire("Error!", "Something went wrong.", "error");
  //     } finally {
  //       setLoading(false);
  //       setCurrent("");
  //     }
  //   }
  // };

  const OnSuccess = (message, updatedSchedules) => {
    if (message) onSubmit(message);
    setCurrent("");
    setShowMarkModal(false);
    setSchedules(updatedSchedules);
  };

  const [activeFollowUp, setActiveFollowUp] = useState(null);

  useEffect(() => {
    if (!followUpID) return;

    setActiveFollowUp(followUpID);

    // Clear after 3 seconds
    const timer = setTimeout(() => {
      setActiveFollowUp(null);
    }, 3000);

    return () => clearTimeout(timer);
  }, [followUpID]);

  return (
    <div className="flex flex-col gap-[14px] w-full max-h-[600px] h-full overflow-auto p-[2px]">
      {/* Header */}
      <Card
        style={{
          padding: "14px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 10,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: T.text,
            letterSpacing: "0.2px",
          }}
        >
          Scheduled Follow-Ups
        </span>

        <ActionButton
          icon="plus"
          label="New Follow-Up"
          variant="filled"
          style={{
            width: "auto",
            padding: "7px 14px",
            fontSize: 12,
            borderRadius: 6,
          }}
          onClick={() => {
            setSelectedActivity(null);
            setShowActivityModal(true);
          }}
        />
      </Card>

      {/* Followups */}
      {leadDetail?.schedules?.length ? (
        leadDetail?.schedules?.map((f) => {
          const color = activityColors[f?.type?.toLowerCase()] || "#8b5cf6";

          return (
            <>
              <Card
                key={f?.id}
                hover
                onClick={() => {
                  if (f?.status?.toLowerCase() !== "completed") {
                    setSelectedActivity(f);
                    setShowActivityModal(true);
                  }
                }}
                style={{
                  padding: "16px 18px",
                  cursor: "pointer",
                  borderLeft: `4px solid ${color}`,
                  borderRadius: 10,
                  transition: "all .2s ease",
                  background: activeFollowUp == f.id ? color + "15" : "#FFFFFF",
                }}
                className={`
    transition-all duration-300
    ${activeFollowUp == f.id ? `!animate-pulse` : ""}
  `}
              >
                <div
                  className={`flex items-start gap-[14px]`}
                  ref={(el) => {
                    followUpRefs.current[f?.id] = el;
                    if (el) console.log("Ref set for", f.id, el);
                  }}
                >
                  {/* Activity Icon */}
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: color + "15",
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      name={activityIcons[f?.type?.toLowerCase() || "default"]}
                      size={15}
                      color={color}
                    />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Title + Status Row */}
                    <div className="flex items-start justify-between gap-2">
                      <div
                        className="capitalize"
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: T.text,
                          lineHeight: 1.3,
                          // textDecoration:
                          //   f.status?.toLowerCase() === "completed"
                          //     ? "line-through"
                          //     : "none",
                          opacity:
                            f.status?.toLowerCase() === "completed" ? 0.6 : 1,
                        }}
                      >
                        {f?.type}
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        {/* Status Badge */}
                        <Badge
                          color={priorityColors[f.status]}
                          style={{
                            fontSize: 10,
                            padding: "3px 8px",
                            borderRadius: 6,
                            textTransform: "capitalize",
                            display: "flex",
                          }}
                        >
                          {f.status}
                        </Badge>
                      </div>
                    </div>

                    {/* Notes */}
                    {f?.note && (
                      <div
                        className="flex gap-1 items-center"
                        style={{
                          color: T.textMuted,
                          marginTop: "4px",
                        }}
                      >
                        <span className="text-gray-600 font-semibold text-xs !w-[56px]">
                          Message:
                        </span>
                        <div
                          title={f?.note}
                          style={{
                            fontSize: 12,
                            color: T.textMuted,
                            lineHeight: 1.4,
                            maxWidth: "90%",
                          }}
                        >
                          {truncateText(f?.note, 30)}
                        </div>
                      </div>
                    )}

                    {f?.reason && (
                      <>
                        <hr />
                        <div
                          className="flex gap-1 items-center"
                          style={{
                            fontSize: 12,
                            color: T.textMuted,
                            lineHeight: 1.4,
                          }}
                        >
                          <span className="text-gray-600 font-semibold !w-[56px]">
                            Reason:
                          </span>
                          <span>{f?.reason}</span>
                        </div>
                      </>
                    )}

                    <div className="flex justify-between items-center">
                      {/* Date Time Row */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 14,
                          marginTop: 8,
                          fontSize: 11,
                          color: T.textMuted,
                          flexWrap: "wrap",
                        }}
                      >
                        <span className="flex items-center gap-[5px]">
                          <Icon name="calendar" size={11} color={T.textMuted} />
                          {f?.due_date}
                        </span>

                        <span className="flex items-center gap-[5px]">
                          <Icon name="clock" size={11} color={T.textMuted} />
                          {f?.due_time}
                        </span>

                        <Badge
                          color={priorityColors[f.priority]}
                          style={{
                            fontSize: 10,
                            padding: "3px 8px",
                            borderRadius: 6,
                            textTransform: "capitalize",
                          }}
                        >
                          {f.priority}
                        </Badge>
                      </div>

                      {/* Mark Completed Button */}
                      {/* {f.status?.toLowerCase() === "pending" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkCompleted(f.id);
                            }}
                            disabled={loading}
                            className={`
      flex items-center gap-1
      bg-white
      border
      border-green-500
      text-green-600
      font-medium
      text-xs
      px-3
      py-1
      rounded-full
      shadow-sm
      hover:bg-green-50
      transition
      duration-200
      disabled:opacity-50
      disabled:cursor-not-allowed
    `}
                          >
                            {loading ? "Updating..." : "Mark Completed!"}
                          </button>
                        )} */}
                      <div className="flex flex-col items-end mt-2 pr-2">
                        {/* Mark Completed Button */}
                        {f.status?.toLowerCase() === "pending" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // handleMarkCompleted(f.id);
                              setShowMarkModal(true);
                              setCurrent(f.id);
                            }}
                            title="Mark Completed"
                            disabled={loading}
                            className={`
        flex items-center justify-center
        bg-white
        border
        border-green-500
        text-green-600
        font-semibold
        text-sm
        w-10
        h-6
        rounded-lg
        shadow-sm
        hover:bg-green-100
        hover:scale-110
        transition
        duration-200
        disabled:opacity-50
        disabled:cursor-not-allowed
      `}
                            style={{
                              opacity: loading && current == f?.id ? 0.5 : 1,
                              transition: "opacity 0.3s ease",
                            }}
                          >
                            {loading && current == f?.id ? (
                              <Icon
                                name="spinner"
                                size={16}
                                className="animate-spin font-semibold"
                              />
                            ) : (
                              <Icon
                                name="check"
                                size={16}
                                className="font-semibold"
                              />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </>
          );
        })
      ) : (
        <div className="flex items-center justify-center ">
          <p className="text-gray-400 text-sm italic">
            No scheduled activities
          </p>
        </div>
      )}

      {/* Modal */}
      {showActivityModal && (
        <FollowUpPopup
          onClose={() => {
            setShowActivityModal(false);
            setSelectedActivity({});
          }}
          CustomerData={selectedActivity}
          tabIndex={2}
          onError={onError}
          onSubmit={onSubmit}
          id={id}
          entitytype={entitytype}
          entityname={entityname}
          UsersList={UsersList}
        />
      )}

      {showMarkModal && (
        <MarkCompleted
          onClose={() => {
            setShowMarkModal(false);
            setCurrent("");
          }}
          onError={onError}
          onSubmit={OnSuccess}
          followUpID={current}
          schedules={schedules}
        />
      )}
    </div>
  );
};

export const FollowUpsTabNew = ({
  leadDetail,
  onError,
  onSubmit,
  UsersList = [],
  id = "",
  entitytype = "",
  entityname = "",
}) => {
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Header */}
        <Card
          style={{
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: 10,
          }}
        >
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: T.text,
              letterSpacing: "0.2px",
            }}
          >
            Scheduled Follow-Ups
          </span>

          <ActionButton
            icon="plus"
            label="New Follow-Up"
            variant="filled"
            style={{
              width: "auto",
              padding: "7px 14px",
              fontSize: 12,
              borderRadius: 6,
            }}
            onClick={() => {
              setSelectedActivity(null);
              setShowActivityModal(true);
            }}
          />
        </Card>

        {leadDetail?.schedules?.length ? (
          <div className="space-y-3">
            <DynamicTable
              data={leadDetail?.schedules}
              rowKey="FollowUpsid"
              onRowClick={(row) => {
                if (row) {
                  setSelectedActivity(row);
                  setShowActivityModal(true);
                }
              }}
              columns={[
                {
                  key: "note",
                  label: "Notes",
                  icon: NotebookPen, // icon for header
                  copy: true,
                  render: (row) => row?.note ?? "-",
                },
                {
                  key: "priority",
                  label: "Priority",
                  icon: ChartNoAxesColumnIncreasing, // icon for header
                  render: (row) => {
                    return (
                      <div
                        className={`inline-flex items-center  gap-1 rounded-[6px]  px-[9px] py-[3px] text-xs font-medium 
                          
                       `}
                        style={{
                          color: priorityColors[row?.priority],
                          backgroundColor: priorityColors[row?.priority] + "14",
                        }}
                      >
                        {row?.priority ?? "-"}
                      </div>
                    );
                  },
                },
                {
                  key: "type",
                  label: "Type",
                  icon: ClipboardType, // icon for header
                },
                {
                  key: "due_date",
                  label: "Due Date",
                  icon: Calendar, // icon for header
                },
                {
                  key: "due_time",
                  label: "Due Time",
                  icon: Clock, // icon for header
                },
                {
                  key: "status",
                  label: "Status",
                  icon: ChartNoAxesCombined, // icon for header
                  render: (row) => {
                    return (
                      <div
                        className={`inline-flex items-center  gap-1 rounded-[6px]  px-[9px] py-[3px] text-xs font-medium 
                          
                        `}
                        style={{
                          color: priorityColors[row?.status],
                          backgroundColor: priorityColors[row?.priority] + "14",
                        }}
                      >
                        {row?.status ?? "-"}
                      </div>
                    );
                  },
                },
              ]}
            />
          </div>
        ) : (
          <p className="text-gray-400 text-sm italic text-center">
            No schedules found.
          </p>
        )}
      </div>

      {/* Modal */}
      {showActivityModal && (
        <FollowUpPopup
          onClose={() => {
            setShowActivityModal(false);
            setSelectedActivity({});
          }}
          CustomerData={selectedActivity}
          tabIndex={2}
          onError={onError}
          onSubmit={onSubmit}
          id={id}
          entitytype={entitytype}
          entityname={entityname}
          UsersList={UsersList}
        />
      )}
    </>
  );
};
export const TimelineTab = ({
  leadDetail,
  onError,
  onSubmit,
  UsersList,
  condition,
  MainIndex = "",
}) => {
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [type, setType] = useState("");
  return (
    <div>
      {leadDetail?.activities?.length ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
              position: "relative",
            }}
          >
            {/* Quick log bar */}

            <Card
              style={{
                padding: "12px 16px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}
              >
                Quick log:
              </span>

              {[
                "Call",
                "Meeting",
                "Email",
                "Follow-up",
                "Demo",
                "Chat",
                "Consultation",
                "Presentation",
              ].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    if (typeof setSelectedActivity === "function") {
                      setSelectedActivity(null);
                      setType(type);
                      console.log("type", type);
                    }
                    if (typeof setShowActivityModal === "function") {
                      setShowActivityModal(true);
                      setType(type);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "5px 12px",
                    borderRadius: 20,
                    border: `1px solid ${activityColors[type?.toLocaleLowerCase() || "default"]}25`,
                    background:
                      activityColors[type?.toLocaleLowerCase()] + "08",
                    color: activityColors[type?.toLocaleLowerCase()],
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "inherit",
                  }}
                >
                  <Icon
                    name={activityIcons[type?.toLocaleLowerCase() || "default"]}
                    size={12}
                    color={
                      activityColors[type?.toLocaleLowerCase() || "default"]
                    }
                  />
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </Card>

            {/* Timeline items */}
            <div style={{ position: "relative", paddingLeft: 28 }}>
              {/* Vertical line */}
              <div
                style={{
                  position: "absolute",
                  left: 11,
                  // top: 15,
                  top: 25,
                  bottom: 40,
                  width: 2,
                  background: "#ddd8d2",
                  borderRadius: 1,
                }}
              />

              {leadDetail?.activities?.map((a, i) => {
                const color =
                  activityColors[a?.icon?.toLocaleLowerCase()] ||
                  "#8b5cf6" ||
                  "#6b7280";
                return (
                  <div
                    key={a.id}
                    style={{ position: "relative", marginBottom: 8 }}
                  >
                    {/* Dot */}
                    <div
                      style={{
                        position: "absolute",
                        left: -22,
                        // top: 16,
                        top: 25,
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: color + "20",
                        border: `2.5px solid ${color}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                      }}
                    >
                      <div
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          background: color,
                        }}
                      />
                    </div>

                    <Card hover style={{ padding: "14px 18px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: color + "12",
                            flexShrink: 0,
                          }}
                        >
                          <Icon
                            name={
                              activityIcons[
                                a?.icon?.toLocaleLowerCase() || "default"
                              ]
                            }
                            size={14}
                            color={color}
                          />
                          {/* <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 14,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: `linear-gradient(135deg, ${T.accent}, #8b5cf6)`,
                              color: "#fff",
                              fontSize: 18,
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(a.user.name) || "U"}
                          </div> */}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {a.log}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 12,
                              marginTop: 5,
                              fontSize: 11,
                              color: T.textMuted,
                            }}
                          >
                            <span>{a?.logtime}</span>
                            <span>by {a.user.name}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <p className="text-gray-400 text-sm italic text-center">
          No timeline available.
        </p>
      )}

      {showActivityModal && (
        <FollowUpPopup
          onClose={() => {
            setShowActivityModal(false);
            setSelectedActivity({});
          }}
          CustomerData={{
            entity_type:
              MainIndex == "2"
                ? "project"
                : MainIndex == "1"
                  ? "deal"
                  : condition == "account"
                    ? "contact"
                    : condition,
            entity_name: leadDetail?.name,
            entity_id:
              MainIndex == "2"
                ? leadDetail?.projectid
                : MainIndex == "1"
                  ? leadDetail?.dealid
                  : MainIndex == "0"
                    ? leadDetail?.userid
                    : leadDetail?.leadid,
          }}
          tabIndex={2}
          onError={onError}
          onSubmit={onSubmit}
          UsersList={UsersList}
          type={type.toLowerCase()}
        />
      )}
    </div>
  );
};
